import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, combineLatestWith, takeUntil } from 'rxjs';
import { Company } from 'src/app/models/customer.model';
import { PaginationData } from 'src/app/models/helpers.model';
import {
  CustomerItemTranslation,
  ItemLanguage,
} from 'src/app/models/item-language.model';
import { Permission } from 'src/app/models/permission.model';
import { PermissionMode } from 'src/app/enums/permission-mode';
import { ItemLanguagesService } from 'src/app/services/item-languages.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { OrderStage } from 'src/app/enums/order-stage';
import { HeaderStateService } from 'src/app/services/header-state.service';

// One grid row: an item number with its base description and one cell per language.
interface TranslationGridRow {
  itemExternalKey: number;
  itemDescription: string;
  cells: Record<string, CustomerItemTranslation | null>;
}

// ECDEV-249: the customer's reusable translation library — reviewed and corrected
// here, outside of any order. Edits affect future orders only: orders already
// submitted keep the text they were saved with (per-order snapshot, ECDEV-246).
@Component({
  selector: 'app-customer-item-translations',
  templateUrl: './customer-item-translations.component.html',
  styleUrls: ['./customer-item-translations.component.scss'],
})
export class CustomerItemTranslationsComponent implements OnInit, OnDestroy {
  @Input() customer!: Company;
  // True inside the customer detail tab, false on the standalone Items page. Only the
  // page owns the dashboard header, so only it publishes the count badge.
  @Input() isAdmin = false;

  languages: ItemLanguage[] = [];
  translations: CustomerItemTranslation[] = [];
  rows: TranslationGridRow[] = [];
  filteredRows: TranslationGridRow[] = [];
  // The grid is paged client-side: the whole library arrives in one call, so there is
  // nothing to fetch per page. app-paginator only needs the counts to render.
  pagedRows: TranslationGridRow[] = [];
  paginationData!: PaginationData;
  private pageSize = 15;
  private currentPage = 1;

  searchString = '';
  isLoaded = false;

  // Stage 1 "Item Translations" permission of the current user's role:
  // Edit allows corrections, View renders read-only, Hide blocks the tab content.
  // A role with no row configured falls back to View (see ngOnInit).
  mode: PermissionMode | null = null;
  permissionMode = PermissionMode;

  isEditModal = false;
  isDeleteModal = false;
  selected: {
    row: TranslationGridRow;
    language: ItemLanguage;
    translation: CustomerItemTranslation;
  } | null = null;
  editText = '';

  private destroy = new Subject<boolean>();

  constructor(
    private itemLanguagesService: ItemLanguagesService,
    private permissionsService: PermissionsService,
    private userService: UserService,
    private toastr: ToastrService,
    private loadingService: LoadingService,
    private headerState: HeaderStateService
  ) {}

  ngOnInit(): void {
    this.userService
      .getCurrentUser()
      .pipe(
        combineLatestWith(this.permissionsService.getPermissions()),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([profile, permissions]: [any, Permission[]]) => {
          const row = permissions.find(
            (p) =>
              p.roleId === profile?.role?.id &&
              p.objectRefName === 'OrderDto' &&
              p.fieldPath === 'ItemTranslations' &&
              p.stage === OrderStage.Stage1
          );
          // ECDEV-249: a missing row is NOT "no access". The backend seeds this
          // field's prop as Show and ApplyPermissions only overlays rows that
          // exist, so an unconfigured role already sees the translations read-only
          // on the order form (Stage 1 > Items). Falling back to View keeps this
          // page consistent with that; only an explicit Hide row blocks the content.
          this.mode = row ? row.mode : PermissionMode.View;

          if (this.isVisible) {
            this.loadData();
          } else {
            this.isLoaded = true;
            this.updateHeaderBadge();
          }
        },
        error: () => (this.isLoaded = true),
      });
  }

  get isVisible(): boolean {
    return (
      this.mode === PermissionMode.Edit || this.mode === PermissionMode.View
    );
  }

  get isEditable(): boolean {
    return this.mode === PermissionMode.Edit;
  }

  loadData(): void {
    this.itemLanguagesService
      .getLanguages()
      .pipe(
        combineLatestWith(
          this.itemLanguagesService.getCustomerTranslations(this.customer.id)
        ),
        takeUntil(this.destroy)
      )
      .subscribe({
        next: ([languages, translations]) => {
          // Columns: every configured ACTIVE language except the English base —
          // the base description already has its own column (same rule as the
          // order form). A language added later appears here automatically.
          this.languages = languages.filter(
            (l) => l.isActive && l.name.toLowerCase() !== 'english'
          );
          this.translations = translations;
          this.buildRows();
          this.isLoaded = true;
        },
        error: () => (this.isLoaded = true),
      });
  }

  private buildRows(): void {
    const byKey = new Map<number, TranslationGridRow>();

    for (const t of this.translations) {
      let row = byKey.get(t.itemExternalKey);
      if (!row) {
        row = {
          itemExternalKey: t.itemExternalKey,
          itemDescription: t.itemDescription,
          cells: {},
        };
        byKey.set(t.itemExternalKey, row);
      }

      if (!row.itemDescription && t.itemDescription) {
        row.itemDescription = t.itemDescription;
      }

      row.cells[t.itemLanguageId] = t;
    }

    this.rows = [...byKey.values()].sort(
      (a, b) => a.itemExternalKey - b.itemExternalKey
    );
    this.applySearch();
    this.updateHeaderBadge();
  }

  // Counts item numbers, not translation cells: one row is one item, however many
  // languages it has been translated into.
  private updateHeaderBadge(): void {
    if (this.isAdmin) {
      return;
    }
    if (!this.isVisible) {
      this.headerState.setBadge(null);
      return;
    }
    const count = this.rows.length;
    this.headerState.setBadge(
      `${count.toLocaleString('en-US')} ${count === 1 ? 'item' : 'items'}`
    );
  }

  applySearch(): void {
    const q = this.searchString.trim().toLowerCase();
    this.filteredRows = !q
      ? [...this.rows]
      : this.rows.filter(
          (r) =>
            String(r.itemExternalKey).includes(q) ||
            r.itemDescription.toLowerCase().includes(q)
        );
    this.currentPage = 1;
    this.applyPagination();
  }

  private applyPagination(): void {
    const totalCount = this.filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / this.pageSize));
    // Deleting the last row of the last page would otherwise leave the grid empty.
    this.currentPage = Math.min(this.currentPage, totalPages);

    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);

    // A fresh object every time: app-paginator reads its input in ngOnChanges, which
    // only fires on a new reference.
    this.paginationData = {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalCount,
      totalPages,
      hasPreviousPage: this.currentPage > 1,
      hasNextPage: this.currentPage < totalPages,
    };
  }

  getPageSettings(pageSettings: { perPage: number; currentPage: number }): void {
    this.pageSize = pageSettings.perPage;
    this.currentPage = pageSettings.currentPage;
    this.applyPagination();
  }

  cellFor(
    row: TranslationGridRow,
    language: ItemLanguage
  ): CustomerItemTranslation | null {
    return row.cells[language.id] ?? null;
  }

  openEditModal(row: TranslationGridRow, language: ItemLanguage): void {
    const translation = this.cellFor(row, language);
    if (!translation || !this.isEditable) return;

    this.selected = { row, language, translation };
    this.editText = translation.text;
    this.isEditModal = true;
  }

  closeEditModal(): void {
    this.isEditModal = false;
    this.selected = null;
  }

  get isEditSaveDisabled(): boolean {
    return (
      !this.selected ||
      this.editText.trim().length === 0 ||
      this.editText.trim() === this.selected.translation.text
    );
  }

  saveEdit(): void {
    if (!this.selected || this.isEditSaveDisabled) return;

    this.loadingService.changeLoadingStatus(true);
    this.itemLanguagesService
      .updateCustomerTranslation(
        this.customer.id,
        this.selected.translation.id,
        this.editText.trim()
      )
      .subscribe({
        next: () => {
          this.toastr.success('Translation updated', 'Success');
          this.loadingService.changeLoadingStatus(false);
          this.closeEditModal();
          this.loadData();
        },
        error: () => this.loadingService.changeLoadingStatus(false),
      });
  }

  backdropEditClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closeEditModal();
    }
  }

  openDeleteModal(row: TranslationGridRow, language: ItemLanguage): void {
    const translation = this.cellFor(row, language);
    if (!translation || !this.isEditable) return;

    this.selected = { row, language, translation };
    this.isDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.isDeleteModal = false;
    this.selected = null;
  }

  confirmDelete(): void {
    if (!this.selected) return;

    this.loadingService.changeLoadingStatus(true);
    this.itemLanguagesService
      .deleteCustomerTranslation(this.customer.id, this.selected.translation.id)
      .subscribe({
        next: () => {
          this.toastr.success('Translation deleted', 'Success');
          this.loadingService.changeLoadingStatus(false);
          this.closeDeleteModal();
          this.loadData();
        },
        error: () => this.loadingService.changeLoadingStatus(false),
      });
  }

  backdropDeleteClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closeDeleteModal();
    }
  }

  ngOnDestroy(): void {
    if (!this.isAdmin) {
      this.headerState.setBadge(null);
    }
    this.destroy.next(true);
    this.destroy.complete();
  }
}
